import { parse, stringify } from 'yaml';
import * as yup from 'yup';

import {
  ContinuousRebalancingConfig,
  KentAggressiveModeConfig,
  KentContinuousRebalancingConfigMode,
  KentEvictionConfig,
  KentEvictionSelectorSettings,
  KentFeatureCompatibilityEntry,
  KentLabelSelector,
} from '@cast/types-kent';
import { isPositiveNumber } from '@cast/utils';

import {
  advancedEvictorConfigurationSchema,
  labelSelectorSchema,
} from 'features/cluster/autoscaler/_constants/advancedEvictorConfiguration';
import {
  DEFAULT_KENT_AGGRESSIVE_MODE_CONFIG,
  DEFAULT_DRAIN_TIMEOUT_MINUTES,
} from 'features/cluster/rebalance/constants';
import { isAtLeastOneAggressiveModeConfigEnabled } from 'features/cluster/rebalance/utils';
import { notReachable } from 'utils/types/not-reachable';

import {
  DEFAULT_DRAIN_ONLY_CYCLE_INTERVAL_SECONDS,
  DEFAULT_DELETE_EMPTY_CYCLE_INTERVAL_SECONDS,
  DEFAULT_FULL_MODE_CYCLE_INTERVAL_SECONDS,
  YAML_INDENTATION,
} from './_constants';
import { FormValues } from './_types';
import { parseAggressiveModeDrainTimeoutSecondsToMinutes } from 'features/cluster/rebalance/utils/aggressiveMode';
import isNumber from 'lodash/isNumber';

type EvictorConfig = yup.InferType<typeof advancedEvictorConfigurationSchema>;
type EvictorLabelSelector = yup.InferType<typeof labelSelectorSchema>;

type ParseEvictionConfigResult =
  | { type: 'success'; data: EvictorConfig }
  | { type: 'error'; error: unknown };

export const parseEvictionConfig = (
  yaml: string
): ParseEvictionConfigResult => {
  try {
    const data = parse(yaml);
    advancedEvictorConfigurationSchema.validateSync(data);
    return { type: 'success', data };
  } catch (error) {
    return { type: 'error', error };
  }
};

const mapContinuousRebalancingConfigModeToFormValue = ({
  mode,
  hasOutdatedKentroller,
}: {
  mode?: KentContinuousRebalancingConfigMode;
  hasOutdatedKentroller: boolean;
}): FormValues['mode'] => {
  if (hasOutdatedKentroller) {
    switch (mode) {
      case 'EMPTY_NODE_REMOVAL':
      case 'BIN_PACKING':
        return mode;
      // Mode should always be available in theory, but the API contract has this field as optional.
      // Fall back to BIN_PACKING (matches outdated kentroller behavior on backend)
      default:
        return 'BIN_PACKING';
    }
  }

  switch (mode) {
    case 'EMPTY_NODE_REMOVAL':
    case 'BIN_PACKING':
    case 'CONTINUOUS_REBALANCING_CYCLE':
      return mode;
    default:
      return 'CONTINUOUS_REBALANCING_CYCLE';
  }
};

const mapKentLabelSelector = (
  labelSelector?: KentLabelSelector
): EvictorLabelSelector => {
  return {
    ...(labelSelector?.matchLabels && {
      matchLabels: labelSelector.matchLabels,
    }),
    ...(labelSelector?.matchExpressions && {
      matchExpressions: labelSelector.matchExpressions.map(
        (matchExpression) => ({
          key: matchExpression.key,
          operator: matchExpression.operator,
          values: matchExpression.values,
        })
      ),
    }),
  };
};

const mapKentSettings = (
  settings?: KentEvictionSelectorSettings
): EvictorConfig['evictionConfig'][number]['settings'] => {
  if (!settings) return null;

  return {
    ...(settings.aggressive && {
      aggressive: { enabled: settings.aggressive.enabled ?? false },
    }),
    ...(settings.disposable && {
      disposable: { enabled: settings.disposable.enabled ?? false },
    }),
    ...(settings.removalDisabled && {
      removalDisabled: { enabled: settings.removalDisabled.enabled ?? false },
    }),
  };
};

export const mapKentEvictionConfigToEvictionConfig = (
  config: KentEvictionConfig
): EvictorConfig => {
  return {
    evictionConfig: (config.customSelectors ?? []).map((item) => ({
      ...(item.nodeSelector && {
        nodeSelector: {
          labelSelector: mapKentLabelSelector(item.nodeSelector.labelSelector),
        },
      }),
      ...(item.podSelector && {
        podSelector: {
          kind: item.podSelector.kind,
          namespace: item.podSelector.namespace,
          replicasMin: item.podSelector.replicasMin,
          labelSelector: mapKentLabelSelector(item.podSelector.labelSelector),
        },
      }),
      settings: mapKentSettings(item.settings),
    })),
  };
};

export const mapContinuousRebalancingConfigToFormValues = ({
  config,
  hasOutdatedKentroller,
}: {
  config?: ContinuousRebalancingConfig;
  hasOutdatedKentroller: boolean;
}): FormValues => {
  const aggressiveModeConfig = config?.aggressiveModeConfig;
  const atLeastOneAggressiveModeConfigEnabled =
    !!aggressiveModeConfig &&
    isAtLeastOneAggressiveModeConfigEnabled(aggressiveModeConfig);

  return {
    enabled: config?.enabled ?? false,
    mode: mapContinuousRebalancingConfigModeToFormValue({
      mode: config?.mode,
      hasOutdatedKentroller,
    }),
    fullModeCycleIntervalSeconds:
      config?.fullModeCycleIntervalSeconds ||
      DEFAULT_FULL_MODE_CYCLE_INTERVAL_SECONDS,
    drainOnlyCycleIntervalSeconds:
      config?.drainOnlyCycleIntervalSeconds ||
      DEFAULT_DRAIN_ONLY_CYCLE_INTERVAL_SECONDS,
    deleteEmptyCycleIntervalSeconds:
      config?.deleteEmptyCycleIntervalSeconds ||
      DEFAULT_DELETE_EMPTY_CYCLE_INTERVAL_SECONDS,
    minNodeAgeSeconds: config?.minNodeAgeSeconds,
    savingsThresholdEnabled: isPositiveNumber(config?.savingsThresholdPercent),
    savingsThresholdPercent: config?.savingsThresholdPercent,
    advancedConfiguration: config?.evictionConfig
      ? stringify(
          mapKentEvictionConfigToEvictionConfig(config?.evictionConfig),
          null,
          YAML_INDENTATION
        )
      : '',
    aggressiveModeEnabled: atLeastOneAggressiveModeConfigEnabled,
    aggressiveModeConfig: atLeastOneAggressiveModeConfigEnabled
      ? {
          aggressiveEviction: aggressiveModeConfig.aggressiveEviction ?? false,
          ignoreBlockingPdb: aggressiveModeConfig.ignoreBlockingPdb ?? false,
          ignoreConsolidatable:
            aggressiveModeConfig.ignoreConsolidatable ?? false,
          ignoreLocalPersistentVolumes:
            aggressiveModeConfig.ignoreLocalPersistentVolumes ?? false,
          ignoreProblemJobPods:
            aggressiveModeConfig.ignoreProblemJobPods ?? false,
          ignoreProblemPodsWithoutController:
            aggressiveModeConfig.ignoreProblemPodsWithoutController ?? false,
          ignoreProblemRemovalDisabledNodes:
            aggressiveModeConfig.ignoreProblemRemovalDisabledNodes ?? false,
          ignoreProblemRemovalDisabledPods:
            aggressiveModeConfig.ignoreProblemRemovalDisabledPods ?? false,
          ignoreZeroNodePoolDisruptionBudgets:
            aggressiveModeConfig.ignoreZeroNodePoolDisruptionBudgets ?? false,
          drainTimeoutMinutes:
            parseAggressiveModeDrainTimeoutSecondsToMinutes(
              aggressiveModeConfig.drainTimeoutSeconds
            ) ?? DEFAULT_DRAIN_TIMEOUT_MINUTES,
        }
      : /*
          Cover the case when aggressive mode is initially off and the user decides to enable it.
          Default values are set so the user doesn't have to go to advanced configuration and enable them manually.
          If not set, aggressive mode won't be enabled, since enabling aggressiveMode in the form has no effect—
          the aggressiveMode flag is deprecated and not sent to the server.
        */
        {
          aggressiveEviction:
            DEFAULT_KENT_AGGRESSIVE_MODE_CONFIG.aggressiveEviction,
          ignoreBlockingPdb:
            DEFAULT_KENT_AGGRESSIVE_MODE_CONFIG.ignoreBlockingPdb,
          ignoreConsolidatable:
            DEFAULT_KENT_AGGRESSIVE_MODE_CONFIG.ignoreConsolidatable,
          ignoreLocalPersistentVolumes:
            DEFAULT_KENT_AGGRESSIVE_MODE_CONFIG.ignoreLocalPersistentVolumes,
          ignoreProblemJobPods:
            DEFAULT_KENT_AGGRESSIVE_MODE_CONFIG.ignoreProblemJobPods,
          ignoreProblemPodsWithoutController:
            DEFAULT_KENT_AGGRESSIVE_MODE_CONFIG.ignoreProblemPodsWithoutController,
          ignoreProblemRemovalDisabledNodes:
            DEFAULT_KENT_AGGRESSIVE_MODE_CONFIG.ignoreProblemRemovalDisabledNodes,
          ignoreProblemRemovalDisabledPods:
            DEFAULT_KENT_AGGRESSIVE_MODE_CONFIG.ignoreProblemRemovalDisabledPods,
          ignoreZeroNodePoolDisruptionBudgets:
            DEFAULT_KENT_AGGRESSIVE_MODE_CONFIG.ignoreZeroNodePoolDisruptionBudgets,
          drainTimeoutMinutes: undefined,
        },
  };
};

const mapLabelSelector = (
  labelSelector: EvictorLabelSelector
): KentLabelSelector => {
  return {
    matchLabels: labelSelector.matchLabels,
    matchExpressions: labelSelector.matchExpressions?.map(
      (matchExpression) => ({
        key: matchExpression.key,
        operator: matchExpression.operator,
        values: matchExpression.values?.filter(
          (value): value is string => typeof value === 'string'
        ),
      })
    ),
  };
};

export const mapEvictionConfigToKentEvictionConfig = (
  config: EvictorConfig
): KentEvictionConfig => {
  return {
    customSelectors: config.evictionConfig.map((item) => ({
      nodeSelector: item.nodeSelector
        ? { labelSelector: mapLabelSelector(item.nodeSelector.labelSelector) }
        : undefined,
      podSelector: item.podSelector
        ? {
            kind: item.podSelector.kind,
            namespace: item.podSelector.namespace,
            replicasMin: item.podSelector.replicasMin,
            labelSelector: mapLabelSelector(item.podSelector.labelSelector),
          }
        : undefined,
      settings: item.settings ?? undefined,
    })),
  };
};

export const mapFormEvictionConfigToApiEvictionConfig = (
  configString: FormValues['advancedConfiguration']
): KentEvictionConfig | undefined => {
  if (!configString) {
    return;
  }

  const result = parseEvictionConfig(configString);

  switch (result.type) {
    case 'success':
      return mapEvictionConfigToKentEvictionConfig(result.data);
    case 'error':
      return;
    default:
      return notReachable(result);
  }
};

const mapDrainTimeoutMinutesToSeconds = (
  minutes: FormValues['aggressiveModeConfig']['drainTimeoutMinutes']
): KentAggressiveModeConfig['drainTimeoutSeconds'] => {
  return isNumber(minutes) ? String(minutes * 60) : undefined;
};

export const formValuesToContinuousRebalancingConfig = (
  values: FormValues
): ContinuousRebalancingConfig => {
  return {
    enabled: values.enabled,
    fullModeCycleIntervalSeconds: values.fullModeCycleIntervalSeconds,
    drainOnlyCycleIntervalSeconds: values.drainOnlyCycleIntervalSeconds,
    deleteEmptyCycleIntervalSeconds: values.deleteEmptyCycleIntervalSeconds,
    minNodeAgeSeconds: values.minNodeAgeSeconds,
    mode: values.mode,
    ...(values.savingsThresholdEnabled
      ? {
          savingsThresholdPercent: values.savingsThresholdPercent,
        }
      : {}),
    evictionConfig: mapFormEvictionConfigToApiEvictionConfig(
      values.advancedConfiguration
    ),
    ...(values.aggressiveModeEnabled
      ? {
          aggressiveModeConfig: {
            aggressiveEviction: values.aggressiveModeConfig.aggressiveEviction,
            ignoreBlockingPdb: values.aggressiveModeConfig.ignoreBlockingPdb,
            ignoreConsolidatable:
              values.aggressiveModeConfig.ignoreConsolidatable,
            ignoreLocalPersistentVolumes:
              values.aggressiveModeConfig.ignoreLocalPersistentVolumes,
            ignoreProblemJobPods:
              values.aggressiveModeConfig.ignoreProblemJobPods,
            ignoreProblemPodsWithoutController:
              values.aggressiveModeConfig.ignoreProblemPodsWithoutController,
            ignoreProblemRemovalDisabledNodes:
              values.aggressiveModeConfig.ignoreProblemRemovalDisabledNodes,
            ignoreProblemRemovalDisabledPods:
              values.aggressiveModeConfig.ignoreProblemRemovalDisabledPods,
            ignoreZeroNodePoolDisruptionBudgets:
              values.aggressiveModeConfig.ignoreZeroNodePoolDisruptionBudgets,
            drainTimeoutSeconds: values.aggressiveModeConfig.aggressiveEviction
              ? mapDrainTimeoutMinutesToSeconds(
                  values.aggressiveModeConfig.drainTimeoutMinutes
                )
              : undefined,
          },
        }
      : {}),
  };
};

export const isKentrollerIncompatibleWithContinuousRebalancing = (
  featureCompatibilityEntries: KentFeatureCompatibilityEntry[]
): boolean => {
  const featureCompatibility = featureCompatibilityEntries.find(
    (entry) => entry.type === 'CONTINUOUS_REBALANCING'
  );

  return featureCompatibility?.compatibility?.status !== 'COMPATIBLE';
};
